/* One dropdown for the whole app. Every native <select> keeps its element, value, labels, layout
   and change handlers; this module only restyles its closed state (select-menus.css) and, on
   pointer devices, opens the shared Tools-style menu in place of the operating system's popup.
   Touch phones keep the native picker, which is the better control there. Selects created later
   (lazy feature modules, re-rendered panels) are picked up by a DOM observer, so no owner has to
   opt in. The Settings modal enhances its own AI selects and the font picker; those are skipped. */
(function initializeMemoraSelectMenus(global) {
    'use strict';
    const d = global.document;
    if (!d || global.MemoraSelectMenus) return;

    const MENU_ID = 'memora-select-menu';
    const MENU_MAX_HEIGHT = 320;
    const MENU_MIN_WIDTH = 180;
    // Hidden plumbing and developer controls stay native.
    const SKIP_IDS = new Set([
        'ai-pane-mode',
        'ai-expl-pdf-select',
        'pdf-tutor-dev-source-mode',
        'memora-test-turn-selector',
        'font-select'
    ]);
    const enhanced = new WeakSet();
    let current = null;
    let typeahead = { text: '', at: 0 };

    const coordinator = () => global.MemoraModalCoordinator || null;

    function usesNativePicker() {
        const body = d.body;
        if (body?.classList.contains('phone-device')) return true;
        return !!global.matchMedia?.('(hover: none) and (pointer: coarse)').matches;
    }

    function shouldEnhance(select) {
        if (!(select instanceof HTMLSelectElement)) return false;
        if (select.multiple || select.size > 1) return false;
        if (SKIP_IDS.has(select.id)) return false;
        if (select.hasAttribute('data-native-select') || select.closest('[data-native-select]')) return false;
        // Settings renders its own trigger for these.
        if (select.hasAttribute('data-memora-ai-setting')
            || select.dataset.settingsEnhanced
            || select.classList.contains('settings-native-select')) return false;
        return true;
    }

    function enhance(select) {
        if (enhanced.has(select) || !shouldEnhance(select)) return false;
        enhanced.add(select);
        select.classList.add('memora-select');
        select.addEventListener('mousedown', onSelectPointer);
        select.addEventListener('keydown', onSelectKeydown);
        return true;
    }

    function enhanceWithin(root = d) {
        if (!root) return;
        if (root instanceof HTMLSelectElement) {
            enhance(root);
            return;
        }
        if (typeof root.getElementsByTagName !== 'function') return;
        const selects = root.getElementsByTagName('select');
        for (let index = 0; index < selects.length; index += 1) enhance(selects[index]);
    }

    function onSelectPointer(event) {
        const select = event.currentTarget;
        if (event.button !== 0 || select.disabled || usesNativePicker()) return;
        // Suppresses the native popup; the select still takes focus like a button would.
        event.preventDefault();
        if (current?.select === select) {
            closeMenu({ restoreFocus: true });
            return;
        }
        try { select.focus({ preventScroll: true }); } catch (_error) { select.focus?.(); }
        openMenu(select);
    }

    function onSelectKeydown(event) {
        const select = event.currentTarget;
        if (select.disabled || usesNativePicker() || current?.select === select) return;
        const opens = event.key === 'Enter'
            || event.key === ' '
            || event.key === 'F4'
            || event.key === 'ArrowDown'
            || event.key === 'ArrowUp';
        if (!opens || event.ctrlKey || event.metaKey) return;
        event.preventDefault();
        openMenu(select);
    }

    function getOptionLabel(option) {
        return String(option.label || option.textContent || '').replace(/\s+/g, ' ').trim();
    }

    function buildOptionButton(select, option) {
        const item = d.createElement('button');
        item.type = 'button';
        item.className = 'memora-select-option';
        item.setAttribute('role', 'option');
        const selected = option.selected;
        item.setAttribute('aria-selected', String(selected));
        if (selected) item.classList.add('is-selected');
        item.disabled = option.disabled;
        item.dataset.optionIndex = String(option.index);
        const label = d.createElement('span');
        label.className = 'memora-select-option-label';
        label.textContent = getOptionLabel(option) || ' ';
        item.appendChild(label);
        item.addEventListener('click', () => choose(select, option.index));
        return item;
    }

    function buildMenu(select) {
        const menu = d.createElement('div');
        menu.id = MENU_ID;
        menu.className = 'memora-select-menu';
        menu.setAttribute('role', 'listbox');
        const labelText = select.getAttribute('aria-label')
            || select.labels?.[0]?.textContent?.replace(/\s+/g, ' ').trim()
            || select.title
            || '';
        if (labelText) menu.setAttribute('aria-label', labelText);
        Array.from(select.children).forEach((child) => {
            if (child instanceof HTMLOptGroupElement) {
                const visible = Array.from(child.children).filter((option) => option instanceof HTMLOptionElement && !option.hidden);
                if (!visible.length) return;
                const heading = d.createElement('div');
                heading.className = 'memora-select-group-label';
                heading.setAttribute('role', 'presentation');
                heading.textContent = child.label || '';
                menu.appendChild(heading);
                visible.forEach((option) => menu.appendChild(buildOptionButton(select, option)));
            } else if (child instanceof HTMLOptionElement && !child.hidden) {
                menu.appendChild(buildOptionButton(select, child));
            }
        });
        menu.addEventListener('keydown', onMenuKeydown);
        return menu;
    }

    function positionMenu(select, menu) {
        const rect = select.getBoundingClientRect();
        const viewportWidth = global.innerWidth || d.documentElement.clientWidth;
        const viewportHeight = global.innerHeight || d.documentElement.clientHeight;
        const width = Math.min(Math.max(rect.width, MENU_MIN_WIDTH), viewportWidth - 16);
        menu.style.width = `${Math.round(width)}px`;
        const desired = Math.min(menu.scrollHeight, MENU_MAX_HEIGHT);
        const below = Math.max(0, viewportHeight - rect.bottom - 12);
        const above = Math.max(0, rect.top - 12);
        const openAbove = below < desired && above > below;
        const height = Math.max(72, Math.min(desired, openAbove ? above : below));
        menu.style.maxHeight = `${Math.round(height)}px`;
        const left = Math.max(8, Math.min(rect.left, viewportWidth - width - 8));
        menu.style.left = `${Math.round(left)}px`;
        menu.style.top = `${Math.round(openAbove ? Math.max(8, rect.top - height - 6) : rect.bottom + 6)}px`;
        menu.dataset.placement = openAbove ? 'above' : 'below';
    }

    function focusOption(button) {
        if (!button) return;
        try { button.focus({ preventScroll: true }); } catch (_error) { button.focus?.(); }
        button.scrollIntoView?.({ block: 'nearest' });
    }

    function getEnabledOptions() {
        return current ? Array.from(current.menu.querySelectorAll('.memora-select-option:not(:disabled)')) : [];
    }

    function focusSelectedOption() {
        if (!current) return false;
        focusOption(current.menu.querySelector('.memora-select-option.is-selected:not(:disabled)') || getEnabledOptions()[0]);
        return true;
    }

    function openMenu(select) {
        closeMenu();
        if (!select.isConnected || !select.options.length) return;
        const menu = buildMenu(select);
        d.body.appendChild(menu);
        positionMenu(select, menu);
        select.setAttribute('aria-expanded', 'true');
        select.setAttribute('aria-controls', MENU_ID);
        select.classList.add('is-menu-open');
        current = { select, menu };
        typeahead = { text: '', at: 0 };
        d.addEventListener('pointerdown', onOutsidePointer, true);
        d.addEventListener('scroll', onOutsideScroll, true);
        global.addEventListener('resize', onViewportChange);
        global.addEventListener('blur', onViewportChange);
        const layers = coordinator();
        if (layers?.open) {
            layers.open({
                id: MENU_ID,
                element: menu,
                opener: select,
                trapFocus: false,
                isOpen: () => !!current && current.menu === menu,
                focus: () => focusSelectedOption(),
                close: () => {
                    closeMenu({ fromCoordinator: true });
                    return true;
                }
            });
        }
        focusSelectedOption();
    }

    function closeMenu({ restoreFocus = false, fromCoordinator = false } = {}) {
        if (!current) return false;
        const { select, menu } = current;
        current = null;
        menu.remove();
        select.removeAttribute('aria-expanded');
        select.removeAttribute('aria-controls');
        select.classList.remove('is-menu-open');
        d.removeEventListener('pointerdown', onOutsidePointer, true);
        d.removeEventListener('scroll', onOutsideScroll, true);
        global.removeEventListener('resize', onViewportChange);
        global.removeEventListener('blur', onViewportChange);
        if (!fromCoordinator) coordinator()?.markClosed?.(MENU_ID);
        if (restoreFocus && select.isConnected) {
            try { select.focus({ preventScroll: true }); } catch (_error) { select.focus?.(); }
        }
        return true;
    }

    function choose(select, optionIndex) {
        const option = select.options[optionIndex];
        closeMenu({ restoreFocus: true });
        if (!option || option.disabled) return;
        if (select.selectedIndex === optionIndex) return;
        select.selectedIndex = optionIndex;
        // The events a person picking from the native popup would have produced, so inline
        // onchange attributes and delegated listeners run unchanged.
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function onMenuKeydown(event) {
        if (!current) return;
        const options = getEnabledOptions();
        const index = options.indexOf(d.activeElement);
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            const step = event.key === 'ArrowDown' ? 1 : -1;
            const next = index < 0 ? 0 : Math.min(options.length - 1, Math.max(0, index + step));
            focusOption(options[next]);
        } else if (event.key === 'Home' || event.key === 'End') {
            event.preventDefault();
            focusOption(event.key === 'Home' ? options[0] : options[options.length - 1]);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            closeMenu({ restoreFocus: true });
        } else if (event.key === 'Tab') {
            closeMenu({ restoreFocus: true });
        } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey && event.key !== ' ') {
            // Typeahead, as the native popup offers.
            const now = Date.now();
            typeahead.text = (now - typeahead.at > 700 ? '' : typeahead.text) + event.key.toLowerCase();
            typeahead.at = now;
            const start = index < 0 ? 0 : index + (typeahead.text.length === 1 ? 1 : 0);
            const ordered = options.slice(start).concat(options.slice(0, start));
            const match = ordered.find((button) => button.textContent.trim().toLowerCase().startsWith(typeahead.text));
            if (match) focusOption(match);
        }
    }

    function onOutsidePointer(event) {
        if (!current) return;
        if (current.menu.contains(event.target) || event.target === current.select) return;
        closeMenu();
    }

    function onOutsideScroll(event) {
        if (!current || current.menu.contains(event.target)) return;
        closeMenu();
    }

    function onViewportChange() {
        closeMenu();
    }

    function start() {
        enhanceWithin(d);
        const observer = new MutationObserver((records) => {
            for (const record of records) {
                record.addedNodes.forEach((node) => {
                    if (node.nodeType === 1) enhanceWithin(node);
                });
            }
            if (current && !current.select.isConnected) closeMenu();
        });
        observer.observe(d.body || d.documentElement, { childList: true, subtree: true });
    }

    global.MemoraSelectMenus = { enhance, enhanceWithin, open: openMenu, close: closeMenu };
    if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
})(window);
